import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, StatusBadge, PriorityBadge } from "@/components/StatCard";
import { AlertOctagon, TrendingUp, LifeBuoy, Lightbulb, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import FilingCalendar from "@/components/FilingCalendar";
import LateFeeAlerts from "@/components/LateFeeAlerts";
import OfficeSummaryCard from "@/components/OfficeSummaryCard";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-zinc-500 text-sm">Loading office summary…</div>;
  const t = data.totals;

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">{isAdmin ? "Daily Office Summary" : "My work today"} · {data.date}</div>
          <h1 className="font-heading font-black tracking-tight text-4xl mt-2">{isAdmin ? "Overview" : "My Overview"}</h1>
        </div>
        <div className="text-xs text-zinc-500 max-w-sm text-right">
          {isAdmin
            ? "Auto-generated task sheet · Statutory deadlines highlighted · Overload delegation suggested"
            : "Your daily task sheet · Statutory deadlines highlighted"}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="stat-clients-worked" label="Clients Worked" value={t.clients_worked} />
        <StatCard testid="stat-returns-filed" label="Returns Filed" value={t.returns_filed} />
        <StatCard testid="stat-registrations" label="Registrations Done" value={t.registrations_completed} />
        <StatCard testid="stat-notices-closed" label="Notices Closed" value={t.notices_closed} />
        <StatCard testid="stat-pending" label="Pending Cases" value={t.pending_cases} tone="warn" />
        <StatCard testid="stat-overdue" label="Overdue" value={t.overdue_cases} tone="danger" sub="Reprioritise first thing" />
        <StatCard testid="stat-completed-today" label="Completed Today" value={t.completed_today} tone="good" />
        <StatCard testid="stat-urgent-tomorrow" label="Urgent Tomorrow" value={t.urgent_tomorrow} tone="dark" />
      </div>

      {isAdmin ? <OfficeSummaryCard /> : null}

      {(isAdmin || (user?.allowed_menus || []).includes("clients")) ? (<><LateFeeAlerts /><FilingCalendar /></>) : null}

      {/* Two-column: overdue / urgent tomorrow */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Panel title="Overdue — action required" icon={AlertOctagon} tone="danger" testid="panel-overdue">
          <MiniTable
            emptyText="Nothing overdue. Great work."
            rows={data.overdue}
            cols={[
              ["Client", (r) => r.client_name],
              ["Service", (r) => r.service],
              ["Assignee", (r) => r.assigned_employee_name],
              ["Deadline", (r) => (r.deadline || "").replace("T", " ").slice(0, 16)],
              ["", (r) => <PriorityBadge priority={r.priority} />],
            ]}
          />
        </Panel>
        <Panel title="Urgent for tomorrow" icon={CalendarClock} testid="panel-urgent-tomorrow">
          <MiniTable
            emptyText="No urgent items for tomorrow."
            rows={data.urgent_tomorrow}
            cols={[
              ["Client", (r) => r.client_name],
              ["Service", (r) => r.service],
              ["Assignee", (r) => r.assigned_employee_name],
              ["Deadline", (r) => (r.deadline || "").replace("T", " ").slice(0, 16)],
              ["", (r) => <PriorityBadge priority={r.priority} />],
            ]}
          />
        </Panel>
      </div>

      {/* Performance strip */}
      <Panel title={isAdmin ? "Team performance today" : "My performance today"} icon={TrendingUp} testid="panel-performance">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {data.performance.map((p) => (
            <div key={p.employee_id} data-testid={`perf-${p.employee_id}`} className="border border-zinc-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <Avatar src={p.avatar} name={p.name} size="md" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 truncate">{p.title}</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <Metric label="Tasks" value={p.tasks} />
                <Metric label="Done"  value={p.completed} accent="good" />
                <Metric label="Pend"  value={p.pending} />
                <Metric label="Over"  value={p.overdue} accent={p.overdue ? "danger" : "default"} />
              </div>
              <div className="mt-3">
                <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">Productivity</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-zinc-100">
                    <div className="h-1.5 bg-zinc-900" style={{ width: `${p.productivity}%` }} />
                  </div>
                  <div className="font-mono text-xs tabular w-10 text-right">{p.productivity}%</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Support + suggestions */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {isAdmin ? (
          <Panel title="Employees requiring support" icon={LifeBuoy} tone="warn" testid="panel-support">
            {data.needs_support.length === 0 ? (
              <div className="text-sm text-zinc-500">Nobody is overloaded today.</div>
            ) : (
              <ul className="space-y-2">
                {data.needs_support.map((p) => (
                  <li key={p.employee_id} className="flex items-center justify-between border-b border-zinc-200 pb-2 last:border-b-0">
                    <div>
                      <div className="text-sm font-semibold">{p.name}</div>
                      <div className="text-xs text-zinc-500">{p.title}</div>
                    </div>
                    <div className="text-xs text-zinc-600 font-mono tabular">
                      {p.pending} pending · <span className="text-rose-600">{p.overdue} overdue</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ) : null}
        <Panel title={isAdmin ? "Suggestions" : "Notes for you"} icon={Lightbulb} testid="panel-suggestions">
          <ul className="space-y-2 text-sm">
            {data.suggestions.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 bg-zinc-900 shrink-0" />
                <span className="text-zinc-700">{s}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, tone, testid, children }) {
  const border = tone === "danger" ? "border-rose-200" : tone === "warn" ? "border-amber-200" : "border-zinc-200";
  return (
    <section data-testid={testid} className={`bg-white border ${border} p-6`}>
      <header className="flex items-center gap-2 mb-4">
        {Icon ? <Icon size={16} strokeWidth={1.75} className={tone === "danger" ? "text-rose-600" : "text-zinc-900"} /> : null}
        <h2 className="font-heading font-bold tracking-tight text-lg">{title}</h2>
      </header>
      {children}
    </section>
  );
}

function MiniTable({ rows, cols, emptyText }) {
  if (!rows?.length) return <div className="text-sm text-zinc-500">{emptyText}</div>;
  return (
    <div className="overflow-x-auto"><table className="w-full text-sm">
      <thead>
        <tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500">
          {cols.map((c, i) => <th key={i} className="text-left font-semibold pb-2">{c[0]}</th>)}
        </tr>
      </thead>
      <tbody className="stripe-rows">
        {rows.map((r, ri) => (
          <tr key={ri} className="border-t border-zinc-100">
            {cols.map((c, ci) => <td key={ci} className="py-2 pr-2">{c[1](r)}</td>)}
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

function Metric({ label, value, accent = "default" }) {
  const color = { default: "text-zinc-900", good: "text-emerald-700", danger: "text-rose-700" }[accent];
  return (
    <div>
      <div className={`font-heading font-bold text-lg tabular ${color}`}>{value}</div>
      <div className="text-[9px] tracking-[0.16em] uppercase text-zinc-500 font-semibold">{label}</div>
    </div>
  );
}
